import { useMemo } from 'react'

import { SerializedTreeNode } from 'modules/job_catalog/components/JobCatalogTree/types'

import { CostCenterConfigurations, JobCatalogStructures } from 'modules/cost_centers/lib/types'

export type JobCatalogFilterOption = {
  label: string
  value: string
}

/**
 * Hook to build Job Catalog filter options based on Cost Center configuration
 *
 * @param configurations - The cost center configurations
 * @param structureData - The structure data containing job catalog trees and locations
 * @returns Array of filter options with label and value (node_uuid)
 */
export const useJobCatalogFilterOptions = (
  configurations: CostCenterConfigurations,
  jobCatalogStructures?: JobCatalogStructures
): JobCatalogFilterOption[] => {
  return useMemo(() => {
    if (!configurations?.length || !jobCatalogStructures?.length) {
      return []
    }

    // Find the highest level JC structure in configurations (only one JC structure allowed)
    const jcConfig = configurations.find((config) => config.structure.startsWith('job_catalog_'))

    if (!jcConfig) {
      return []
    }

    const structureLevel = jcConfig.structure

    // Parse the serialized tree
    const tree = jobCatalogStructures[0]
    if (!tree?.serializedTree) {
      return []
    }

    const parsedTree: SerializedTreeNode = JSON.parse(tree.serializedTree)
    const options: JobCatalogFilterOption[] = []

    // Build options based on the structure level
    switch (structureLevel) {
      case 'job_catalog_family':
        options.push(...buildFamilyOptions(parsedTree))
        break
      case 'job_catalog_function':
        options.push(...buildFunctionOptions(parsedTree))
        break
      case 'job_catalog_role':
        options.push(...buildRoleOptions(parsedTree))
        break
    }

    return options
  }, [configurations, jobCatalogStructures])
}

/**
 * Build options for family level
 */
const buildFamilyOptions = (tree: SerializedTreeNode): JobCatalogFilterOption[] => {
  const options: JobCatalogFilterOption[] = []

  if (tree.children) {
    for (const family of tree.children) {
      if (family.type === 'jobcatalog_treefamily') {
        options.push({
          label: family.name,
          value: family.node_uuid,
        })
      }
    }
  }

  return options
}

/**
 * Build options for function level with hierarchy (family / function)
 */
const buildFunctionOptions = (tree: SerializedTreeNode): JobCatalogFilterOption[] => {
  const options: JobCatalogFilterOption[] = []

  if (tree.children) {
    for (const family of tree.children) {
      if (family.type === 'jobcatalog_treefamily' && family.children) {
        for (const func of family.children) {
          if (func.type === 'jobcatalog_treefunction') {
            options.push({
              label: `${family.name} / ${func.name}`,
              value: func.node_uuid,
            })
          }
        }
      }
    }
  }

  return options
}

/**
 * Build options for role level with full hierarchy (family / function / role)
 */
const buildRoleOptions = (tree: SerializedTreeNode): JobCatalogFilterOption[] => {
  const options: JobCatalogFilterOption[] = []

  if (tree.children) {
    for (const family of tree.children) {
      if (family.type === 'jobcatalog_treefamily' && family.children) {
        for (const func of family.children) {
          if (func.type === 'jobcatalog_treefunction' && func.children) {
            for (const role of func.children) {
              if (role.type === 'jobcatalog_treerole') {
                options.push({
                  label: `${family.name} / ${func.name} / ${role.name}`,
                  value: role.node_uuid,
                })
              }
            }
          }
        }
      }
    }
  }

  return options
}
